import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AuthCallback from "./pages/AuthCallback";
import Login from "./pages/Login";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import AdminPanel from "./pages/AdminPanel";
import { Wallet, LogOut } from "lucide-react";
import { Button } from "./components/ui/button";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function Blocked() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0F172A] text-white p-6">
      <div className="max-w-md text-center">
        <div className="w-14 h-14 rounded-2xl bg-red-500/20 text-red-400 flex items-center justify-center mx-auto mb-5">
          <Wallet className="w-7 h-7" />
        </div>
        <h1 className="font-display text-2xl font-bold">Access not granted</h1>
        <p className="text-slate-400 mt-2 text-sm">
          Hi {user?.name?.split(" ")[0] || "there"}, your ePitaka account request was declined by an administrator.
          If you think this is a mistake, please contact support.
        </p>
        <Button onClick={logout} variant="outline" className="mt-6 border-slate-600 text-slate-200 hover:bg-white/10" data-testid="blocked-logout-button">
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </Button>
      </div>
    </div>
  );
}

function Protected() {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <AdminPanel />;
  if (user.status === "rejected") return <Blocked />;
  if (!user.onboarding_completed) return <Onboarding />;
  return <Dashboard />;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/" element={<Protected />} />
      <Route path="/onboarding" element={<Protected />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
